<script module>
    import { navigationManager } from '$lib/navigation/navigation-manager.js'

    // Backward-compat exports for AdminLayout and router context
    export const router = navigationManager

    // Reactive state wrappers — updated via subscription in <script>
    let Component = $state(null)
    let is404 = $state(false)
    export const route = $state({ path: '', search: '', params: null })

</script>

<script>
    import { session } from './store.svelte.js';
    import { ModeWatcher, mode } from 'mode-watcher';
    import { setContext } from 'svelte';

    import RouteOutlet from '$lib/navigation/RouteOutlet.svelte';
    import RouteA11y from '$lib/navigation/RouteA11y.svelte';
    import Lazy from '$lib/components/Lazy.svelte';
    import { commandPaletteState, bindCommandPaletteShortcut } from '$lib/components/command-palette.svelte.js';
    import { initGestures } from '$lib/navigation/gesture.js';
    import { injectChromeStyles } from '$lib/navigation/chrome.js';
    import LoadingRoot from '$lib/loading/LoadingRoot.svelte';
    import LoadingOverlay from '$lib/loading/components/LoadingOverlay.svelte';
    import { gradeState, loadYears, preferredYear } from '$lib/stores/grade.svelte.js';
    import { preferences } from '$lib/stores/preferences.svelte.js';
    import { record_client_event } from '$lib/api-calls.js';
    import { captureAcquisition } from '$lib/acquisition.js';
    import { track as trackMetaEvent } from '$lib/tracking/meta-pixel.js';
    import { DEFAULT_DESTINATION, nextFrom } from '$lib/auth-intent.js';

    captureAcquisition()

    injectChromeStyles()

    setContext('router', navigationManager);

    let isLandingPreview = $derived(route.path.startsWith('/admin/landing/preview'));
    let isAdminPage = $derived(route.path.startsWith('/admin') && !isLandingPreview);
    let isLandingPage = $derived(route.path === '/' || isLandingPreview);
    // Practice sessions and the question reader run in a focus mode — hide the
    // global mobile bottom nav so their own Prev/Next docked bar can sit at
    // bottom-0 without being covered by app chrome. Both own a 100dvh column.
    const READER_PATH = /^(?:\/g\/\d+|\/reading)(?:\/lectures\/[^/]+)?\/questions\/[^/]+$/;
    let isFocusPage = $derived(
        /^\/practice\/sessions\/[^/]+$/.test(route.path) || READER_PATH.test(route.path)
    );

    // Where a signed-out visitor can go from a catalog page. Without these the
    // bar carried no navigation off the landing page at all: the logo is the
    // only way home and on a phone the menu was empty but for the CTA.
    const PUBLIC_LINKS = [
        { label: 'Home', href: '/' },
        { label: 'Courses', href: '/courses' },
        { label: 'Books', href: '/books' },
    ];

    // The landing page and the auth callback both own their whole viewport:
    // one has a fixed navbar of its own, the other is a transient technical
    // screen where a year switcher and an avatar mean nothing.
    const routesWithoutHeader = [/^\/$/, /^\/auth-callback$/, /^\/login$/];
    // Catalog URLs, which a signed-out visitor sees as landing-style pages
    // carrying their own navbar and footer. Signed in, the same URLs are app
    // pages and keep the app chrome.
    //
    // Matched against the URL, not the route pattern: `route.path` carries the
    // resolved path, which is why the checks around it are written as regexes
    // too. The book pattern excludes /book/<id>/read — the reader is the one
    // thing under a book that still needs an account.
    const CATALOG_PATHS = [
        /^\/courses$/,
        /^\/course\/\d+(?:\/[^/]*)?$/,
        /^\/books$/,
        /^\/book\/\d+(?:\/(?!read$)[^/]*)?$/,
    ];
    const isCatalogPath = (path) => CATALOG_PATHS.some(pattern => pattern.test(path));

    // Paths a visitor without a session can see. The catalog is here because
    // the server renders these same pages into the document for crawlers
    // (main/seo) — if the app then demanded a login on the URL a search result
    // points at, the page a person gets and the page a crawler was shown would
    // be two different things. The pages themselves are the app's own: they
    // read the public endpoints when there is no session.
    // `/auth-callback` has to render without a session, because running before
    // there is one is its whole job. Returning from Google with a first-time
    // account, allauth leaves a pending `provider_signup` flow and the session
    // is deliberately not authenticated yet — AuthCallback.svelte is what
    // completes it. Gated like any other route, it rendered the login page
    // instead, so signing in with Google bounced straight back to the form.
    // `/terms` is the terms of sale: a buyer, a payment provider or anyone
    // asked to check them must be able to read them without an account.
    const isPublicPath = (path) =>
        path === '/' || path === '/auth-callback' || path === '/login' || path === '/terms'
        || isCatalogPath(path);

    // A signup that skipped the register form — Google — arrives with no
    // faculty and no study year, and every listing filters by both. The app has
    // nothing to show such an account, so it renders the setup step instead of
    // the router until the question is answered. Superusers are exempt:
    // `createsuperuser` cannot set academic fields and admin is not scoped by
    // them.
    let needsAcademicSetup = $derived(
        session.is_authenticated
        && !session.is_superuser
        && session.user?.needs_academic_setup === true
    );

    // A signed-out catalog page is a landing surface: it brings its own navbar
    // and footer, so the app header, the safe-area padding and the mobile tab
    // bar all stand down, exactly as they do on the landing page itself.
    let isPublicCatalogPage = $derived(
        !session.is_authenticated && isCatalogPath(route.path)
    );
    // The callback screen is a full-viewport status message: no header, no
    // tab bar, nothing to navigate to until it knows where to send you.
    let isAuthCallback = $derived(route.path === '/auth-callback');
    // The sign-in screen owns its whole viewport, like the callback does.
    let isAuthScreen = $derived(isAuthCallback || route.path === '/login');
    // The terms page is a standalone document with its own back button.
    let isLegalPage = $derived(route.path === '/terms');
    let isChromeless = $derived(isLandingPage || isPublicCatalogPage || isAuthScreen || isLegalPage);

    // `/` is a public URL but it is only a landing page for signed-out users.
    // Once bootstrap says this visitor already has a session, never mount the
    // landing route while we resolve their study year and replace the URL with
    // the grade home. Keeping the loading surface up for the whole redirect
    // prevents the signed-in root load from flashing Landing between requests.
    let isAuthenticatedRootRedirect = $derived(
        session.is_authenticated && (route.path === '/' || route.path === '/app')
    );

    let showHeader = $derived(
        !routesWithoutHeader.some(regex => regex.test(route.path)) && !isPublicCatalogPage
    );

    // Subscribe to NavigationManager — keeps $state wrappers in sync
    $effect(() => {
        const unsub = navigationManager.subscribe((s) => {
            Component = s.component;
            is404 = s.is404;
            route.path = s.route.path;
            route.search = s.route.search;
            route.params = s.route.params;
        });
        return unsub;
    });

    // The palette's shortcut is bound here rather than inside the component:
    // the component is fetched on first use, and a shortcut that only works
    // after the thing it opens has loaded would never fire.
    const palette = commandPaletteState();
    $effect(bindCommandPaletteShortcut);

    // Initialize gesture system (edge back swipe on mobile)
    $effect(() => {
        const cleanup = initGestures()
        return cleanup
    });

    // Pageview tracking — fire-and-forget on each committed navigation.
    // Endpoint is rate-limited + metadata-sanitized server-side.
    $effect(() => navigationManager.afterNavigate((intent) => {
        const path = intent?.to || intent?.route?.path || location.pathname
        // Ads land signed out, so Meta counts every visitor; admin is not traffic.
        if (!path.startsWith('/admin')) trackMetaEvent('PageView')
        if (!session.is_authenticated) return
        record_client_event('pageview', {
            path,
            from: intent?.from || '',
            referrer: document.referrer || '',
        }).catch(() => {})
    }))

    // Kick off grade list fetch once authenticated so redirects can pick a
    // year that actually has content instead of a hard fallback.
    $effect(() => {
        if (session.loaded && session.is_authenticated) loadYears()
    });

    // Whenever the user is browsing under /g/{N}/..., remember N as their
    // preferred year so redirects (root, legacy) land there next visit.
    $effect(() => {
        const m = route.path.match(/^\/g\/(\d+)(?:\/|$)/)
        if (!m) return
        const n = Number(m[1])
        if (Number.isFinite(n) && preferences.currentYear !== n) {
            preferences.currentYear = n
        }
    });
    // Signed in already and sitting on the sign-in screen — a stale tab, or a
    // just-completed sign-in. Either way it is not where they meant to end up,
    // and unlike the rewrites below this does not need the year list first.
    $effect(() => {
        if (!session.loaded || !session.is_authenticated) return
        if (route.path !== '/login') return
        navigationManager.replace(nextFrom(route.search) || DEFAULT_DESTINATION)
    });
    $effect(() => {
        if (!session.loaded || !session.is_authenticated) return
        if (!gradeState.loaded) return
        const p = route.path
        const yr = preferredYear()
        if (p === '/' || p === '/app') {
            navigationManager.replace(`/g/${yr}`)
            return
        }
        const rewrite =
            p === '/courses' ? `/g/${yr}/courses` :
            p === '/books' ? `/g/${yr}/books` :
            p === '/playlists' ? `/g/${yr}/questions` :
            p.startsWith('/reading/lectures/') ? `/g/${yr}${p.slice('/reading'.length)}` :
            p.startsWith('/reading/questions/') ? `/g/${yr}${p.slice('/reading'.length)}` :
            null
        if (rewrite) navigationManager.replace(rewrite)
    });
</script>

<LoadingRoot />

{#if session.loaded && !route.path}
    <!-- Session bootstrap may finish before the initial route chunk commits.
         Keep the same full-page loading surface visible through that handoff
         instead of exposing a blank frame. -->
    <LoadingOverlay message="" fullPage={true} />
{:else if session.loaded && route.path}
    {#if needsAcademicSetup}
        <ModeWatcher />
        <Lazy loader={() => import('$lib/components/auth/AcademicSetup.svelte')} />
    {:else if isAuthenticatedRootRedirect}
        <ModeWatcher />
        <LoadingOverlay message="" fullPage={true} />
    {:else if isPublicPath(route.path) || session.is_authenticated}
        <ModeWatcher />

        {#if isAdminPage}
            {#if session.is_superuser && !is404}
                <Lazy loader={() => import('$lib/layouts/AdminLayout.svelte')}
                      Component={Component} params={route.params} />
            {:else}
                <Lazy loader={() => import('./pages/NotFound.svelte')} />
            {/if}
        {:else}
            <!-- viewport-fit=cover + black-translucent status bar means the web
                 view runs under the notch in the installed app. The header is
                 is part of normal flow before it becomes sticky. Landing,
                 practice sessions and the reader manage their own top edge (a
                 fixed navbar, a 100dvh column) and pad it internally instead. -->
            <div class="flex w-full flex-col {isFocusPage ? 'h-[100dvh] overflow-hidden' : 'min-h-screen'} {isChromeless || isFocusPage ? '' : 'pt-[env(safe-area-inset-top,0px)]'}">
                {#if showHeader}
                    {#if isFocusPage}
                        <!-- On focus routes the page toolbar already owns the
                             phone/tablet top safe area. Rendering the app's
                             mobile header above it duplicates that inset and
                             burns a large part of the answering viewport. Keep
                             the global header only once its desktop row takes
                             over at lg. This is web-shell chrome only; the
                             Telegram entry has its own shell. -->
                        <div class="hidden lg:contents">
                            <Lazy loader={() => import('$lib/components/Header.svelte')}
                                  currentPath={route.path} />
                        </div>
                    {:else}
                        <Lazy loader={() => import('$lib/components/Header.svelte')}
                              currentPath={route.path} />
                    {/if}
                {:else if isPublicCatalogPage}
                    <!-- The catalog pages are the same components either way;
                         only the frame differs. Someone arriving from a search
                         result has never seen the app shell, so they get the
                         landing chrome they would have met on the way in. -->
                    <Lazy loader={() => import('$lib/components/landing/Navbar.svelte')}
                          isScrolled={true} links={PUBLIC_LINKS} />
                {/if}

                <!-- The landing navbar is fixed, so a catalog page framed by it
                     has to reserve its height the way the landing page does
                     internally; without it the back button sits under the bar. -->
                <!-- A focus page fills whatever the header leaves rather than
                     subtracting a guessed header height: the header is 86px on
                     a phone and 80px above md, and the guess cost the docked
                     action bar its place on mobile. -->
                <main data-route-content tabindex="-1" class="flex-1 {isFocusPage ? 'flex flex-col min-h-0 overflow-hidden' : ''} {isPublicCatalogPage ? 'pt-24 md:pt-28' : ''} {isChromeless || isFocusPage ? '' : 'pb-16 md:pb-0'}">
                    {#if is404}
                        <Lazy loader={() => import('./pages/NotFound.svelte')} />
                    {:else}
                        <RouteOutlet {Component} params={route.params} path={route.path} />
                    {/if}
                </main>

                {#if isPublicCatalogPage}
                    <Lazy loader={() => import('$lib/components/landing/Footer.svelte')} />
                {/if}
            </div>
        {/if}

        {#if !isChromeless && !isFocusPage}
            <Lazy loader={() => import('$lib/components/MobileBottomBar.svelte')}
                  currentPath={route.path} />
        {/if}

        {#if palette.everOpened}
            <Lazy loader={() => import('$lib/components/CommandPalette.svelte')} />
        {/if}
    {:else}
        <Lazy loader={() => import('./pages/Login.svelte')} />
    {/if}

    <RouteA11y />

    <!-- main.js keeps server-rendered public content visible until this route
         tree has committed, avoiding a blank or transient login handoff. -->
    <span data-app-ready data-app-path={route.path} hidden></span>
{/if}
